import React, { useState, useContext, useCallback } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import ExpandIcon from '@atlaskit/icon/glyph/chevron-down';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import { PopupMenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';

import { CreateDropdown, CreateButton } from './horizontal-create.style';
import { HorizontalNavContext } from './horizontal-nav-context';
import { HorizontalNavLinkItem } from './horizontal-nav-link-item';
import messages from './horizontal-nav.i18n';
import * as styled from './horizontal-nav.style';

type Props = {
  intl: InjectedIntl;
  isBeingRenderedInsideMobileNav?: boolean;
};

let HorizontalCreate: React.FC<Props> = props => {
  const {
    globalBitbucketActions,
    containerBitbucketActions,
    linkComponent,
    isLoggedIn,
    publishUiEvent,
  } = useContext(HorizontalNavContext);
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);
  const onOpen = useCallback(() => {
    setIsOpen(prev => !prev);
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'navigationItem',
      actionSubjectId: 'createButton',
      source: 'navigation',
      attributes: {
        itemIsGlobalHorizontalNav: true,
      },
    });
  }, [setIsOpen, publishUiEvent]);

  const renderMenu = useCallback(
    () => (
      <PopupMenuGroup minWidth={149}>
        {globalBitbucketActions && globalBitbucketActions.length && (
          <Section>
            {globalBitbucketActions.map(menuItem => (
              <HorizontalNavLinkItem
                linkComponent={linkComponent}
                key={menuItem.url}
                href={menuItem.url}
                isClientLink={menuItem.is_client_link}
                target={menuItem.target}
                testId={menuItem?.test_id || undefined}
                analyticsLabel={menuItem.analytics_label}
                actionSubject="navigationGlobalCreateItem"
                publishUiEvent={publishUiEvent}
              >
                {menuItem.label}
              </HorizontalNavLinkItem>
            ))}
          </Section>
        )}
        {containerBitbucketActions && !!containerBitbucketActions.length && (
          <Section
            title={props.intl.formatMessage(messages.repositorySectionHeading)}
          >
            {containerBitbucketActions.map(menuItem => (
              <HorizontalNavLinkItem
                linkComponent={linkComponent}
                key={menuItem.url}
                href={menuItem.url}
                isClientLink={menuItem.is_client_link}
                target={menuItem.target}
                testId={menuItem?.test_id || undefined}
                analyticsLabel={menuItem.analytics_label}
                actionSubject="navigationContainerCreateItem"
                publishUiEvent={publishUiEvent}
                onClose={onClose}
              >
                {menuItem.label}
              </HorizontalNavLinkItem>
            ))}
          </Section>
        )}
      </PopupMenuGroup>
    ),
    [
      globalBitbucketActions,
      containerBitbucketActions,
      linkComponent,
      publishUiEvent,
      props.intl,
    ]
  );

  const renderTriggerButton = useCallback(
    triggerProps => (
      <styled.HorizontalNavItem>
        {props.isBeingRenderedInsideMobileNav ? (
          <CreateButton
            appearance="primary"
            iconAfter={<EditorAddIcon label="" size="medium" />}
            {...triggerProps}
            isSelected={isOpen}
            aria-label={props.intl.formatMessage(
              messages.createButtonAriaLabel
            )}
            testId="mobile-create"
            id="createGlobalItem"
            onClick={onOpen}
          />
        ) : (
          <CreateDropdown
            {...triggerProps}
            appearance="primary"
            isSelected={isOpen}
            aria-label={props.intl.formatMessage(
              messages.createButtonAriaLabel
            )}
            testId="create-button"
            id="createGlobalItem"
            onClick={onOpen}
            iconAfter={<ExpandIcon label="expand" />}
          >
            {props.intl.formatMessage(messages.createButtonLabel)}
          </CreateDropdown>
        )}
      </styled.HorizontalNavItem>
    ),
    [isOpen, onOpen, props.intl, props.isBeingRenderedInsideMobileNav]
  );

  if (
    !isLoggedIn ||
    (!globalBitbucketActions?.length && !containerBitbucketActions?.length)
  ) {
    return null;
  }

  return (
    <styled.HorizontalNavItem>
      <Popup
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
        trigger={renderTriggerButton}
        content={renderMenu}
      />
    </styled.HorizontalNavItem>
  );
};

export default React.memo(injectIntl(HorizontalCreate));

HorizontalCreate = React.memo(injectIntl(HorizontalCreate));

export { HorizontalCreate };
